import axios from 'axios';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/$/, '');

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isLoginRequest = error.config?.url?.includes('/auth/login');

    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }

    if (!error.response) {
      error.message = `Unable to reach the backend API at ${API_BASE_URL}. Make sure the backend server is running.`;
    }

    return Promise.reject(error);
  }
);

// ── Auth ──────────────────────────────────────────────────────
export const authService = {
  login: (email: string, password: string) => api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.put('/auth/change-password', { currentPassword, newPassword }),
  resetPasswordWithToken: (token: string, newPassword: string) =>
    api.post('/auth/reset-password', { token, newPassword }),
};

// ── Dashboard ─────────────────────────────────────────────────
export const dashboardService = {
  getSuperAdmin: () => api.get('/dashboard/super-admin'),
  getFarmManager: () => api.get('/dashboard/farm-manager'),
  getEmployee: () => api.get('/dashboard/employee'),
  getAuditLogs: (params?: any) => api.get('/dashboard/audit-logs', { params }),
};

// ── Users ─────────────────────────────────────────────────────
export const userService = {
  getAll: (params?: any) => api.get('/users', { params }),
  getById: (id: string) => api.get(`/users/${id}`),
  create: (data: any) => api.post('/users', data),
  update: (id: string, data: any) => api.put(`/users/${id}`, data),
  deactivate: (id: string) => api.patch(`/users/${id}/deactivate`),
  activate: (id: string) => api.patch(`/users/${id}/activate`),
  archive: (id: string) => api.patch(`/users/${id}/archive`),
  sendPasswordReset: (id: string) => api.post(`/users/${id}/reset-password`),
  forcePasswordReset: (id: string) => api.patch(`/users/${id}/force-reset`),
};

// ── Farms ─────────────────────────────────────────────────────
export const farmService = {
  getAll: (params?: any) => api.get('/farms', { params }),
  getById: (id: string) => api.get(`/farms/${id}`),
  create: (data: any) => api.post('/farms', data),
  update: (id: string, data: any) => api.put(`/farms/${id}`, data),
  updateStatus: (id: string, status: string) => api.patch(`/farms/${id}/status`, { status }),
  delete: (id: string) => api.delete(`/farms/${id}`),
  getStats: (id: string) => api.get(`/farms/${id}/stats`),
  getSections: (farmId: string) => api.get(`/farms/${farmId}/sections`),
  createSection: (farmId: string, data: any) => api.post(`/farms/${farmId}/sections`, data),
  getFlocks: (farmId: string) => api.get(`/farms/${farmId}/flocks`),
};

// ── Egg Collections ───────────────────────────────────────────
export const eggService = {
  getAll: (params?: any) => api.get('/egg-collections', { params }),
  create: (data: any) => api.post('/egg-collections', data),
  update: (id: string, data: any) => api.put(`/egg-collections/${id}`, data),
  delete: (id: string) => api.delete(`/egg-collections/${id}`),
  getSummary: (params?: any) => api.get('/egg-collections/summary', { params }),
};

// ── Activities ────────────────────────────────────────────────
export const activityService = {
  getAll: (params?: any) => api.get('/activities', { params }),
  create: (data: any) => api.post('/activities', data),
  update: (id: string, data: any) => api.put(`/activities/${id}`, data),
};

// ── Sales ─────────────────────────────────────────────────────
export const salesService = {
  getAll: (params?: any) => api.get('/sales', { params }),
  getById: (id: string) => api.get(`/sales/${id}`),
  create: (data: any) => api.post('/sales', data),
  update: (id: string, data: any) => api.put(`/sales/${id}`, data),
  getSummary: (params?: any) => api.get('/sales/summary', { params }),
  getEggStockBySize: (params: { farm_id: string }) => api.get('/sales/egg-stock-by-size', { params }),
  recordPayment: (id: string, data: any) => api.post(`/sales/${id}/payments`, data),
};

// ── Customers ─────────────────────────────────────────────────
export const customerService = {
  getAll: (params?: any) => api.get('/customers', { params }),
  getById: (id: string) => api.get(`/customers/${id}`),
  create: (data: any) => api.post('/customers', data),
  update: (id: string, data: any) => api.put(`/customers/${id}`, data),
  getLedger: (id: string, params?: any) => api.get(`/customers/${id}/ledger`, { params }),
  getPayments: (id: string) => api.get(`/customers/${id}/payments`),
  getSales: (id: string) => api.get(`/customers/${id}/sales`),
  recordPayment: (id: string, data: any) => api.post(`/customers/${id}/payments`, data),
  getDebtors: (params?: any) => api.get('/customers/debtors', { params }),
};

// ── Creditors ─────────────────────────────────────────────────
export const creditorService = {
  getAll: (params?: any) => api.get('/creditors', { params }),
  getById: (id: string) => api.get(`/creditors/${id}`),
  create: (data: any) => api.post('/creditors', data),
  update: (id: string, data: any) => api.put(`/creditors/${id}`, data),
  getLedger: (id: string, params?: any) => api.get(`/creditors/${id}/ledger`, { params }),
  getPayments: (id: string) => api.get(`/creditors/${id}/payments`),
  addCharge: (id: string, data: any) => api.post(`/creditors/${id}/charges`, data),
  recordPayment: (id: string, data: any) => api.post(`/creditors/${id}/payments`, data),
  getOutstanding: (params?: any) => api.get('/creditors/outstanding', { params }),
};

// ── Expenses & Financials ─────────────────────────────────────
export const expenseService = {
  getAll: (params?: any) => api.get('/expenses', { params }),
  create: (data: any) => api.post('/expenses', data),
  update: (id: string, data: any) => api.put(`/expenses/${id}`, data),
  delete: (id: string) => api.delete(`/expenses/${id}`),
  // Invoice / receipt soft copy, stored privately — viewing goes through a signed URL, never a public link.
  uploadInvoice: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/expenses/${id}/invoice`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getInvoiceUrl: (id: string) => api.get(`/expenses/${id}/invoice`),
  deleteInvoice: (id: string) => api.delete(`/expenses/${id}/invoice`),
};

export const financialService = {
  getProfitLoss: (params?: any) => api.get('/financials/profit-loss', { params }),
  getMonthlyTrend: (params?: any) => api.get('/financials/trend', { params }),
};

// ── Inventory ─────────────────────────────────────────────────
export const inventoryService = {
  getAll: (params?: any) => api.get('/inventory', { params }),
  create: (data: any) => api.post('/inventory', data),
  update: (id: string, data: any) => api.put(`/inventory/${id}`, data),
  recordTransaction: (data: any) => api.post('/inventory/transactions', data),
  getTransactions: (params?: any) => api.get('/inventory/transactions', { params }),
};

// ── Notifications ─────────────────────────────────────────────
export const notificationService = {
  getAll: () => api.get('/notifications'),
  markRead: (ids: string[]) => api.patch('/notifications/read', { ids }),
};

// ── Birds / Flocks ────────────────────────────────────────────
export const birdsService = {
  getStats: (params?: any) => api.get('/birds/stats', { params }),
  getFlocks: (params?: any) => api.get('/birds/flocks', { params }),
  getFlockById: (id: string) => api.get(`/birds/flocks/${id}`),
  createFlock: (data: any) => api.post('/birds/flocks', data),
  updateFlock: (id: string, data: any) => api.put(`/birds/flocks/${id}`, data),
  disposeFlock: (id: string, data: any) => api.patch(`/birds/flocks/${id}/dispose`, data),
  getHealthRecords: (params?: any) => api.get('/birds/health', { params }),
  createHealthRecord: (data: any) => api.post('/birds/health', data),
  recordMovement: (data: any) => api.post('/birds/movements', data),
  getDueHealthCheckpoints: () => api.get('/birds/health-checkpoints/due'),
  getHealthChartContent: () => api.get('/birds/health-chart'),
};

// ── Subscription ──────────────────────────────────────────────
export const subscriptionService = {
  getPlans: () => api.get('/subscription/plans'),
  getMine: () => api.get('/subscription/mine'),
  initializePayment: (plan_name: string) => api.post('/subscription/initialize', { plan_name }),
  verifyPayment: (reference: string) => api.get(`/subscription/verify?reference=${reference}`),
  getPrice: (plan_name: string) => api.get(`/subscription/price?plan_name=${plan_name}`),
  getPaymentHistory: () => api.get('/subscription/payments'),
  getAllSubscriptions: () => api.get('/subscription/all'),
};

// ── Egg Pricing ───────────────────────────────────────────────
export const eggPricingService = {
  getAll: () => api.get('/egg-pricing'),
  create: (data: any) => api.post('/egg-pricing', data),
  update: (id: string, data: any) => api.put(`/egg-pricing/${id}`, data),
  delete: (id: string) => api.delete(`/egg-pricing/${id}`),
};

export default api;